import type { Ref } from 'vue'
import { computed, nextTick, ref } from 'vue'
import { getCandidateStatus, isInterviewFuture, sources } from '@bc-library/recruitment'
import { recruitmentApi } from '@/services/api'
import MdiCheckAll from '~icons/mdi/check-all'
import BiQuestion from '~icons/bi/question'
import FluentMdl2EventDeclined from '~icons/fluent-mdl2/event-declined'
import MdiBan from '~icons/mdi/ban'
import { displayLocale } from '@/services/application/displayLocale'

export const isAbleToEditProcess = (resource, isManaging: Ref<boolean>) => {
  return computed(() => resource.profile?.modules?.recruitment?.canEditNotManaged || isManaging.value)
}

export const populateMeeting = (meeting) => {
  return {
    ...meeting,
    events: meeting.events.map((event) => {
      return {
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
        attendees: event.attendees.map((attendee) => {
          return {
            ...attendee,
            sentDate: attendee.sentDate ? new Date(attendee.sentDate) : attendee.sentDate,
            statusChangeDate: attendee.statusChangeDate ? new Date(attendee.statusChangeDate) : attendee.statusChangeDate,
          }
        }),
      }
    }),
  }
}

export const createNewRound = ({ oneEvaluationPerTurn = false }) => {
  // if (process.rounds[process.rounds.length-1].interviews.length==0) {
  //     process.rounds.splice(process.rounds.length-1, 1);
  // };

  return {
    noGo: null,
    goToNext: null,
    decisionBy: null,
    proposalCanceledDate: null,
    proposalDate: null,
    proposalRejectedDate: null,
    proposalAcceptedDate: null,
    interviews: [],
    oneEvaluation: oneEvaluationPerTurn,
  }
}

// The candidate's own invitation: only its attendee's dates come back (status, sending, answer)
const populateCandidateMeeting = candidateMeeting => ({
  ...candidateMeeting,
  events: (candidateMeeting.events || []).map(event => ({
    ...event,
    attendees: (event.attendees || []).map(attendee => ({
      ...attendee,
      sentDate: attendee.sentDate ? new Date(attendee.sentDate) : attendee.sentDate,
      statusChangeDate: attendee.statusChangeDate ? new Date(attendee.statusChangeDate) : attendee.statusChangeDate,
    })),
  })),
})

export const populateInterview = (currentInterview, interviewIndex) => {
  return {
    ...currentInterview,
    interviewIndex,
    meeting: currentInterview.meeting && typeof currentInterview.meeting !== 'string' ? populateMeeting(currentInterview.meeting) : undefined,
    candidateMeetingPopulated: currentInterview.candidateMeetingPopulated && typeof currentInterview.candidateMeetingPopulated === 'object'
      ? populateCandidateMeeting(currentInterview.candidateMeetingPopulated)
      : currentInterview.candidateMeetingPopulated,
  }
}

export const populateRound = (currentRound, roundIndex) => {
  return {
    ...currentRound,
    candidateCancelled: currentRound.candidateCancelled ? new Date(currentRound.candidateCancelled) : null,
    roundIndex,
    noGo: currentRound.noGo ? new Date(currentRound.noGo) : null,
    goToNext: currentRound.goToNext ? new Date(currentRound.goToNext) : null,
    proposalDate: currentRound.proposalDate ? new Date(currentRound.proposalDate) : null,
    proposalAcceptedDate: currentRound.proposalAcceptedDate ? new Date(currentRound.proposalAcceptedDate) : null,
    proposalRejectedDate: currentRound.proposalRejectedDate ? new Date(currentRound.proposalRejectedDate) : null,
    proposalCanceledDate: currentRound.proposalCanceledDate ? new Date(currentRound.proposalCanceledDate) : null,
    interviews: currentRound.interviews.map(populateInterview),
  }
}

export const populateProcess = (process: any) => {
  const newProcess = {
    ...process,
    hiredDate: process.hiredDate ? new Date(process.hiredDate) : process.hiredDate,
  }

  if (process.nextContact) {
    newProcess.nextContact = new Date(process.nextContact)
  }

  if (process._created)
    newProcess._created = new Date(process._created)

  if (process.candidateSource || process.candidateSource === 0)
    newProcess.candidateSourcePopulated = process.candidateSource || process.candidateSource === 0 ? sources.find(source => source._id === process.candidateSource) : null

  if (process.notes) {
    newProcess.notes = process.notes.map((note) => {
      return {
        ...note,
        _created: new Date(note._created),
      }
    }).sort((a, b) => b._created - a._created)
  }

  if (process.comments) {
    newProcess.comments = process.comments.map((comment) => {
      return {
        ...comment,
        _created: new Date(comment._created),
      }
    }).sort((a, b) => b._created - a._created)
  }

  if (process.rounds)
    newProcess.rounds = process.rounds.map(populateRound)

  if (process.logs) {
    newProcess.logs = process.logs.map((log) => {
      return {
        ...log,
        _created: new Date(log._created),
        meeting: log.meeting
          ? {
            ...log.meeting,
            events: log.meeting.events.map((event) => {
              return {
                ...event,
                start: new Date(event.start),
                end: new Date(event.end),
              }
            }),
          }
          : log.meeting,
      }
    }).sort((a, b) => b._created.getTime() - a._created.getTime())
  }

  if (process.rejectedDate)
    newProcess.rejectedDate = new Date(process.rejectedDate)

  return newProcess
}


export const getroundIndex = (process, interviewId) => {
  return process.rounds.findIndex(round => round.interviews.find(interview => interview._id === interviewId))
}


export const getInterviewIndex = ({ interviews, interviewId }) => {
  return interviews.findIndex(interview => interview._id === interviewId)
}

export const getInterviewRecruiterIndex = ({ interview, recruiterId }: { interview: any; recruiterId: string }) => {
  // return process.rounds[round].interviews.reduce((interviewAcc, interview) => {
  //   if (interview._id !== interviewId)
  //     return interviewAcc
  //   return interviewAcc || interview.recruiters.findIndex(recruiter => recruiter._id === recruiterId)
  // }, null)

  return interview.recruiters.findIndex(recruiter => recruiter._id === recruiterId)
}

export const initEvaluationDisplay = (process, interviewId, recruiterId) => {
  const roundIndex = getroundIndex(process, interviewId)
  const interviewIndex = getInterviewIndex({ interviews: process.rounds[roundIndex].interviews, interviewId })
  const interviewRecruiterIndex = getInterviewRecruiterIndex({ interview: process.rounds[roundIndex].interviews[interviewIndex], recruiterId })

  const round = process.rounds[roundIndex]
  const recruiter = round.interviews[interviewIndex].recruiters[interviewRecruiterIndex]
  const hasEmptyCandidateEvaluation = (evaluation) => !evaluation || evaluation.length === 0

  // Minimal evaluations have no form — only hydrate candidateEvaluation from a populated form list
  if (round.oneEvaluation && hasEmptyCandidateEvaluation(round.candidateEvaluation) && round.evaluationFormPopulated?.list)
    round.candidateEvaluation = round.evaluationFormPopulated.list
  else if (!round.oneEvaluation && hasEmptyCandidateEvaluation(recruiter.candidateEvaluation)) {
    const formList = recruiter.evaluationFormPopulated?.list || process.evaluationFormPopulated?.list
    if (formList)
      recruiter.candidateEvaluation = formList
  }

  return { process, roundIndex, interviewIndex, interviewRecruiterIndex }
}

export const depopulateProcess = (process: any) => {
  const newProcess = {
    ...process,
  }

  if (process.candidateSourcePopulated)
    newProcess.candidateSource = process.candidateSourcePopulated._id

  return newProcess
}

export const isActionAsBeenMadeOnRound = (currentRound) => {
  return currentRound.noGo || currentRound.goToNext || currentRound.proposalDate
}

export const getPdfInterview = async (processId, roundIndex, interviewIndex, pdfFile) => {
  if (pdfFile.value.base64File)
    return
  pdfFile.value.loadingFile = true
  const query = {}
  if (roundIndex > -1)
    query.round = roundIndex

  if (interviewIndex > -1)
    query.interview = interviewIndex

  try {
    const response = await recruitmentApi.getBase64CandidatePdf(processId, query).promise
    pdfFile.value.base64File = response.base64File
  } finally {
    pdfFile.value.loadingFile = false
  }
}


export const STATUS_CONFIG: Record<string, {
  translation: string
  color: string
  icon: any
  detailTranslation?: string
  dateField?: 'sentDate' | 'statusChangeDate'
}> = {
  ACCEPTED: {
    translation: 'components.recruitment.AttendeeStatus.1',
    color: 'success-op',
    icon: MdiCheckAll,
  },
  'NEEDS-ACTION': {
    translation: 'components.recruitment.AttendeeStatus.2',
    color: 'warning-op',
    icon: BiQuestion,
    detailTranslation: 'components.recruitment.AttendeeStatus.5',
    dateField: 'sentDate',
  },
  TENTATIVE: {
    translation: 'components.recruitment.AttendeeStatus.3',
    color: 'primary-op',
    icon: BiQuestion,
    detailTranslation: 'components.recruitment.AttendeeStatus.7',
    dateField: 'statusChangeDate',
  },
  DECLINED: {
    translation: 'components.recruitment.AttendeeStatus.4',
    color: 'error-op',
    icon: FluentMdl2EventDeclined,
    detailTranslation: 'components.recruitment.AttendeeStatus.9',
    dateField: 'statusChangeDate',
  },
  NOT_SENT: {
    translation: 'components.recruitment.AttendeeStatus.0',
    color: 'error-op',
    icon: MdiBan
  }
}


// A recruiter can prepare their evaluation as soon as the interview is planned (fields, notes),
// but decides and finalizes it from 1 hour before the interview. null: no meeting date, nothing to wait for.
export const getEvaluationDecisionOpening = (meeting?: { events?: { start?: Date | string }[] } | string | null) => {
  const start = typeof meeting === 'object' ? meeting?.events?.[0]?.start : null
  if (!start)
    return null

  const opening = new Date(start)
  opening.setHours(opening.getHours() - 1)
  return opening
}

/**
 * Calendar rows: a group interview (Meeting.isMultiple) is one row whatever its number of candidates.
 * They move to sharedCandidates, and the row opens the group interview panel instead of one process.
 */
export const groupSharedSlotRows = (rows: any[]) => {
  const sessions = new Map<string, any>()
  const grouped: any[] = []
  for (const row of rows) {
    if (!row.isMultiple) {
      grouped.push(row)
      continue
    }

    let session = sessions.get(row._id)
    if (!session) {
      session = { ...row, processId: undefined, candidate: undefined, candidateMeetingStatus: undefined, roundIndex: undefined, sharedCandidates: [] }
      sessions.set(row._id, session)
      grouped.push(session)
    }
    if (row.processId)
      session.sharedCandidates.push({ processId: row.processId, candidate: row.candidate, candidateMeetingStatus: row.candidateMeetingStatus, roundIndex: row.roundIndex })
  }
  return grouped
}

// A group interview capacity: a whole number of places, or null for no limit
export const normalizeGroupInterviewCapacity = (value: unknown) => {
  const places = Number(value)
  return value !== null && value !== '' && places >= 1 ? Math.floor(places) : null
}

export const useAttendeeDisplay = (attendeeStatus, meeting) => {

  return computed(() => {


    const formatter = new Intl.DateTimeFormat(displayLocale.value, {
      dateStyle: 'short',
      timeStyle: 'short',
    })

    const { status, sentDate, statusChangeDate } = attendeeStatus

    if (!status || !sentDate) return STATUS_CONFIG.NOT_SENT

    const config = STATUS_CONFIG[status]
    if (!config) return STATUS_CONFIG.NOT_SENT

    let detail = null

    if (isInterviewFuture(meeting) && config.detailTranslation && config.dateField) {

      const targetDate = config.dateField === 'sentDate' ? sentDate : statusChangeDate
      if (targetDate) {
        detail = {
          translation: config.detailTranslation,
          formattedDate: formatter.format(targetDate)
        }
      }
    }

    return {
      translation: config.translation,
      color: config.color,
      icon: config.icon,
      detail
    }
  })
}

export const closeProcess = ({ process, round, user, reason }: {
  process: Ref<any>
  round: Ref<any>
  user: any
  reason?: { _id: string, display: string } | null
}) => {
  process.value.isActive = false
  process.value.rejectedBy = user._id
  process.value.rejectedByPopulated = user
  process.value.rejectedDate = new Date()
  process.value.rejectedCategory = reason?._id || null
  process.value.rejectedCategoryPopulated = reason || null
  process.value.logs.unshift({
    actionId: 50,
    resourceId: user._id,
    resource: user,
  })

  const newStatus = getCandidateStatus(process.value)

  const toSend = {
    isActive: process.value.isActive,
    rejectedBy: process.value.rejectedBy,
    rejectedDate: process.value.rejectedDate,
    rejectedCategory: process.value.rejectedCategory,
    log: { actionId: 50, resourceId: user._id, resource: user },
    newStatus,
  }

  return recruitmentApi.partialUpdateRound({ processId: process.value._id, roundId: round.value._id, round: toSend }).promise.then(() => {
    process.value.currentStatus = newStatus
  })
}

export const useInfinityRecruitmentProcess = ({
  search = ref(''),
  limit = ref(20),
  page = ref(0),
  isLoading = ref(false),
  list = ref([]),
} = {}) => {
  const haveMore = ref(true)

  const getMoreData = async () => {
    try {
      isLoading.value = true
      const searchResponse = await recruitmentApi.candidateBaseList({
        search: search.value,
        limit: limit.value,
        page: page.value,
      }).promise
      haveMore.value = !!searchResponse.count?.hasNextPage
      await nextTick()
      const docs = (searchResponse.docs || []).map((process: any) => {
        const candidateName = process.candidate
          ? `${process.candidate.firstname || ''} ${process.candidate.lastname || ''}`.trim()
          : ''
        return {
          ...process,
          display: candidateName || process.display || 'Candidat',
        }
      })
      list.value = page.value === 0 ? docs : [...list.value, ...docs]
      return searchResponse
    }
    catch (err) {
      console.log('err', err)
    }
    finally {
      isLoading.value = false
    }
  }

  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  }
}
